// src/features/versions/components/VersionForm.tsx

import { useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useVersionStore } from '@/stores/versionStore';
import { useCreateVersion } from '@/hooks/useVersion';
import { CreateVersionInput, versionSchema } from '../types';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { Badge } from '@/components/ui/badge';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { useRegions } from '@/hooks/useRegions';
import { CreateVersionWithRegionInput } from '@/features/regions/types';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';

export function VersionForm() {
  const { providerId, regionId } = useParams<{ providerId: string, regionId?: string }>();
  const { isCreateModalOpen, closeCreateModal, copyProgress, setCopyProgress } = useVersionStore();
  const createVersion = useCreateVersion();
  const [existingDraft, setExistingDraft] = useState<{ id: number, version_number: string, region_id?: number, regionName?: string } | null>(null);
  const [regionVersionNumbers, setRegionVersionNumbers] = useState<Record<number, string>>({});
  
  // Fetch available regions
  const { data: regions = [] } = useRegions(Number(providerId));

  // Define form type based on whether we're coming from a region
  type FormValues = CreateVersionInput & { region_id?: number };

  const form = useForm<FormValues>({
    resolver: zodResolver(versionSchema),
    defaultValues: {
      provider_id: Number(providerId),
      description: '',
      // When in a region context, always use that region ID
      region_id: regionId ? Number(regionId) : undefined,
    }
  });

  const queryClient = useQueryClient();

  // Check for existing drafts when modal opens
  useEffect(() => {
    if (isCreateModalOpen && providerId) {
      const checkExistingDrafts = async () => {
        // When in a region context, always use the region ID from the URL
        const contextRegionId = regionId ? Number(regionId) : form.getValues().region_id;
        
        // Build query based on region selection
        const query = supabase
          .from('provider_versions')
          .select('id, version_number, region_id')
          .eq('provider_id', parseInt(providerId))
          .eq('status', 'draft');
          
        // If we have a region context, check drafts for that specific region only
        if (contextRegionId) {
          query.eq('region_id', contextRegionId);
        } else if (regions && regions.length > 0) {
          // If provider has regions but none selected, don't filter by region_id to show any existing drafts
          // This allows the UI to show a warning if there's any draft in any region
        } else {
          // If no regions exist yet, we'll create a default version without region_id
          // No need to filter as there shouldn't be any drafts yet
        }
        
        const { data, error } = await query;
          
        if (!error && data && data.length > 0) {
          const draft = data[0];
          
          // If there's a region_id, fetch the region name
          if (draft.region_id) {
            const { data: regionData } = await supabase
              .from('regions')
              .select('name')
              .eq('id', draft.region_id)
              .single();
              
            if (regionData) {
              setExistingDraft({
                ...draft,
                regionName: regionData.name
              });
            } else {
              setExistingDraft(draft);
            }
          } else {
            setExistingDraft(draft);
          }
        } else {
          setExistingDraft(null);
        }
      };
      
      checkExistingDrafts();
      
      // Only add a watcher if we're not in a region context
      if (!regionId) {
        const subscription = form.watch((_, { name }) => {
          if (name === 'region_id') {
            checkExistingDrafts();
          }
        });
        
        return () => subscription.unsubscribe();
      }
    }
  }, [isCreateModalOpen, providerId, regionId, form, regions]);

  const { data: nextVersion } = useQuery({
    queryKey: ['next-version-number', providerId, regionId || form.watch('region_id')],
    queryFn: async () => {
      const contextRegionId = regionId ? Number(regionId) : form.getValues().region_id;
      
      // If we have a region context, use the region-specific version number function
      if (contextRegionId) {
        const { data, error } = await supabase
          .rpc('get_next_region_version_number', {
            provider_id_param: Number(providerId),
            region_id_param: contextRegionId
          });
        
        if (error) throw error;
        return data || '1.0';
      } else {
        // Fall back to provider-wide version numbering if no region selected
        const { data, error } = await supabase
          .rpc('get_next_version_number', {
            provider_id_param: Number(providerId)
          });
        
        if (error) throw error;
        return data || '1.0';
      }
    },
    enabled: isCreateModalOpen
  });

  // Add a watcher for region changes to update version number
  useEffect(() => {
    if (isCreateModalOpen && !regionId) {
      const subscription = form.watch((formValues, { name }) => {
        if (name === 'region_id') {
          // Invalidate the version number query to force a refresh
          queryClient.invalidateQueries({ 
            queryKey: ['next-version-number', providerId, formValues.region_id] 
          });
        }
      });
      
      return () => subscription.unsubscribe();
    }
  }, [isCreateModalOpen, providerId, regionId, form, queryClient]);

  // Fetch next version number for each region when modal opens
  useEffect(() => {
    if (isCreateModalOpen && regions.length > 0) {
      const fetchRegionVersionNumbers = async () => {
        const versionNumbersMap: Record<number, string> = {};
        
        // Create an array of promises for each region
        const promises = regions.map(async (region) => {
          try {
            const { data, error } = await supabase
              .rpc('get_next_region_version_number', {
                provider_id_param: Number(providerId),
                region_id_param: region.id
              });
              
            if (!error && data) {
              versionNumbersMap[region.id] = data;
            }
          } catch (err) {
            console.error(`Error fetching version number for region ${region.id}:`, err);
          }
        });
        
        // Wait for all promises to resolve
        await Promise.all(promises);
        setRegionVersionNumbers(versionNumbersMap);
      };
      
      fetchRegionVersionNumbers();
    }
  }, [isCreateModalOpen, providerId, regions]);

  const onSubmit = async (data: FormValues) => {
    try {
      // Reset progress
      setCopyProgress({
        plans: false,
        categories: false,
        channels: false,
        completed: false
      });

      // When in a region context, always use the region ID from the URL
      const submissionData = {
        ...data,
        region_id: regionId ? Number(regionId) : data.region_id
      };

      // Create version with the appropriate region
      await createVersion.mutateAsync(submissionData as CreateVersionWithRegionInput);
      
      // Update progress
      setCopyProgress({
        plans: true,
        categories: true,
        channels: true,
        completed: true
      });

      // Show success message
      toast.success("New version created successfully");

      // Close modal and reset form
      closeCreateModal();
      form.reset();
    } catch (error) {
      setCopyProgress(null);
      
      // Check if this is a draft already exists error
      const errorMessage = error instanceof Error ? error.message : "Failed to create version";
      const isRegionSelectionRequired = errorMessage.includes("Please select a specific region");
      
      if (isRegionSelectionRequired && regions.length > 0 && !regionId) {
        // Show more helpful toast for region selection errors (only when not in a region context)
        toast.error("This provider has multiple regions. Please select a specific region for the new version.");
      } else {
        // Regular error toast
        toast.error(errorMessage);
      }
    }
  };

  return (
    <Dialog open={isCreateModalOpen} onOpenChange={closeCreateModal}>
      <DialogContent aria-describedby="version-creation-description">
        <DialogHeader>
          <DialogTitle>Create New Version {nextVersion && `(${nextVersion})`}</DialogTitle>
          <DialogDescription id="version-creation-description">
            This will create a new draft version copying data from the latest published version.
          </DialogDescription>
          <div className="mt-2">
            <Badge variant="default">Next Version: {nextVersion && `${nextVersion}`}</Badge>
          </div>
        </DialogHeader>

        {existingDraft && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Draft Already Exists</AlertTitle>
            <AlertDescription>
              A draft version ({existingDraft.version_number}) already exists 
              {existingDraft.region_id 
                ? ` for region "${existingDraft.regionName || existingDraft.region_id}"`
                : " for global settings"}.
              Please publish or delete the existing draft before creating a new one.
            </AlertDescription>
            <div className="mt-3">
              <Button
                variant="outline"
                size="sm"
                onClick={closeCreateModal}
              >
                Close
              </Button>
            </div>
          </Alert>
        )}

        {!existingDraft && (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Input 
                        {...field} 
                        placeholder="Version description"
                        disabled={createVersion.isPending}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Only show region selector when not in a region context */}
              {!regionId && regions.length > 0 && (
                <FormField
                  control={form.control}
                  name="region_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Region</FormLabel>
                      <Select
                        onValueChange={(value) => field.onChange(value === "default" ? null : Number(value))}
                        value={field.value?.toString() || "default"}
                        disabled={createVersion.isPending}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select a region" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {regions.length === 0 ? (
                            <SelectItem value="default" disabled>No regions available - will create default</SelectItem>
                          ) : (
                            <>
                              {/* Only show region options with next version numbers */}
                              {regions.map(region => (
                                <SelectItem key={region.id} value={region.id.toString()}>
                                  {region.name}
                                  {region.zip_code && ` (${region.zip_code})`}
                                  {regionVersionNumbers[region.id] && ` - Next: ${regionVersionNumbers[region.id]}`}
                                </SelectItem>
                              ))}
                            </>
                          )}
                        </SelectContent>
                      </Select>
                      <FormDescription>
                        {regions.length === 0 
                          ? "No regions configured - a default region will be created"
                          : "Select a specific region for this version - each region has its own version numbering"}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {/* Show region information when in a region context */}
              {regionId && (
                <div className="rounded-md bg-secondary p-3">
                  <p className="text-sm">
                    This version will be created for the current region.
                  </p>
                </div>
              )}

              {copyProgress && (
                <div className="space-y-2">
                  <Progress 
                    value={
                      ((copyProgress.plans ? 1 : 0) +
                      (copyProgress.categories ? 1 : 0) +
                      (copyProgress.channels ? 1 : 0)) * 33.33
                    } 
                  />
                  <p className="text-sm text-muted-foreground">
                    {copyProgress.completed ? 'Copy completed' : 'Copying data...'}
                  </p>
                </div>
              )}

              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={closeCreateModal}
                  disabled={createVersion.isPending}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={createVersion.isPending}
                >
                  {createVersion.isPending ? 'Creating...' : 'Create Version'}
                </Button>
              </div>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
}